import React, { useState } from 'react'
import { Card, Table, Tag, Button, Space, Modal, Typography, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, MoreOutlined, FileTextOutlined } from '@ant-design/icons'
import { useNotificationTemplates, type NotificationTemplate } from '../hooks/useNotificationTemplates'
import { TemplateFormModal } from '../components/TemplateFormModal'

const { Text } = Typography

export const NotificationTemplatesScreen: React.FC = () => {
    const {
        templates,
        isLoading,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        isCreating,
        isUpdating
    } = useNotificationTemplates()

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null)

    const handleAdd = () => {
        setEditingTemplate(null)
        setIsModalOpen(true)
    }

    const handleEdit = (record: NotificationTemplate) => {
        setEditingTemplate(record)
        setIsModalOpen(true)
    }

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this template?',
            content: 'This action cannot be undone. Any code trying to use this template will fail.',
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => {
                return deleteTemplate(id)
            }
        })
    }

    const handleSubmit = async (values: Partial<NotificationTemplate>) => {
        if (editingTemplate) {
            await updateTemplate({ id: editingTemplate.id, data: values })
        } else {
            await createTemplate(values)
        }
        setIsModalOpen(false)
    }

    const columns = [
        {
            title: 'Event Name',
            dataIndex: 'event_name',
            key: 'event_name',
            render: (text: string) => <Text strong>{text}</Text>,
            sorter: (a: NotificationTemplate, b: NotificationTemplate) => a.event_name.localeCompare(b.event_name),
        },
        {
            title: 'Channel',
            dataIndex: 'channel',
            key: 'channel',
            render: (channel: string) => {
                const colorMap: Record<string, string> = {
                    sms: 'orange',
                    email: 'blue',
                    whatsapp: 'green',
                    push: 'purple'
                }
                return <Tag color={colorMap[channel] || 'default'}>{channel.toUpperCase()}</Tag>
            },
            filters: [
                { text: 'SMS', value: 'sms' },
                { text: 'Email', value: 'email' },
                { text: 'WhatsApp', value: 'whatsapp' },
            ],
            onFilter: (value: boolean | React.Key, record: NotificationTemplate) => record.channel === value,
        },
        {
            title: 'Language',
            dataIndex: 'language',
            key: 'language',
            render: (lang: string) => <Tag>{lang.toUpperCase()}</Tag>,
        },
        {
            title: 'Provider',
            dataIndex: 'provider',
            key: 'provider',
        },
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
            align: 'center' as const,
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'success' : 'default'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right' as const,
            render: (_: any, record: NotificationTemplate) => {
                const items: MenuProps['items'] = [
                    {
                        key: 'edit',
                        icon: <EditOutlined />,
                        label: 'Edit',
                        onClick: () => handleEdit(record)
                    },
                    {
                        type: 'divider'
                    },
                    {
                        key: 'delete',
                        icon: <DeleteOutlined />,
                        label: 'Delete',
                        danger: true,
                        onClick: () => handleDelete(record.id)
                    }
                ]

                return (
                    <Dropdown menu={{ items }} trigger={['click']}>
                        <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                )
            },
        },
    ]

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card
                title={
                    <Space>
                        <FileTextOutlined />
                        Notification Templates
                    </Space>
                }
                extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Add Template
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    dataSource={templates}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{ pageSize: 20 }}
                />
            </Card>

            <TemplateFormModal
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onSubmit={handleSubmit}
                initialValues={editingTemplate}
                loading={isCreating || isUpdating}
            />
        </Space>
    )
}
